import React from 'react';
import { motion } from 'framer-motion';
import { Brush, ShoppingBag, UserCheck, Compass, Crown, Settings } from 'lucide-react';

const ROLES = [
  {
    Icon: Brush,
    label: 'Editor',
    color: '#3B82F6',
    body: 'Your assigned requests, your upload queue, your QC feedback — nothing else.',
  },
  {
    Icon: ShoppingBag,
    label: 'Buyer',
    color: '#F97316',
    body: 'Your briefs, your deliveries, your download codes, your performance numbers.',
  },
  {
    Icon: UserCheck,
    label: 'Team Lead / ATL',
    color: '#10B981',
    body: 'Cross-editor visibility, reassignment, deletion authority, audit trail.',
  },
  {
    Icon: Compass,
    label: 'Vertical Head',
    color: '#8B5CF6',
    body: 'Everything in your vertical: requests, editors, throughput.',
  },
  {
    Icon: Crown,
    label: 'Creative Head',
    color: '#EC4899',
    body: 'Quality bar, QC outcomes, escalation queue.',
  },
  {
    Icon: Settings,
    label: 'Admin',
    color: '#475569',
    body: 'RBAC console, activity log export, system health.',
  },
];

export function HomePersonas() {
  return (
    <section className="relative py-24 md:py-28 bg-[#f8f9fa] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gray-400 font-semibold mb-4">
            Built for every role
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
            One platform, <span className="text-gray-400">six different views.</span>
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Orbit's RBAC means each person sees exactly what they need — and nothing they don't.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ROLES.map((role, i) => (
            <motion.div
              key={role.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: 'easeOut' }}
              whileHover={{ y: -4 }}
              className="group bg-white border border-gray-200 rounded-2xl p-6 transition-shadow hover:shadow-lg"
            >
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105"
                  style={{ backgroundColor: `${role.color}15`, color: role.color }}
                >
                  <role.Icon size={22} />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{role.label}</h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{role.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
